import { motion } from 'framer-motion'
import React, { useState } from 'react'
import { nl2br } from '../../helpers/nl2br'

const M25DetailsAndColors = ({
  lead,
  heading,
  body,
  image,
  video,
  videoRatio,
  isAnimation,
  colorGroups,
  anchorLinkId,
}) => {
  const [videoIsPlayed, setVideoIsPlayed] = useState(false)

  const onPlayVideo = el => {
    setVideoIsPlayed(true)
  }

  let videoRatioDefault = videoRatio || 133
  if (image && image?.length) {
    videoRatioDefault = (image[0].height / image[0].width) * 100
  }

  return (
    <section className="mb-10 md:mb-20" id={anchorLinkId}>
      <div className="container">
        <div className="mb-2 row-type-j">
          <div className="z-20 col-1-type-j">
            <motion.div
              className="u-p-small"
              initial={isAnimation ? { opacity: 0, y: -50 } : { opacity: 1 }}
              animate={{ opacity: 1, y: 0 }}
              exit={isAnimation ? { opacity: 0, y: -50 } : { opacity: 1 }}
              transition={{ opacity: { duration: 0.3 }, duration: 0.5 }}
            >
              {lead}
            </motion.div>
          </div>
          <div className="col-2-type-j" />
        </div>
        <div className="bg-white rounded-[5px] px-6.5 lg:px-0">
          <div className="row-type-j pt-28">
            <div className="z-20 col-1-type-j">
              <motion.h1
                className="mb-6 u-h0"
                initial={
                  isAnimation ? { opacity: 0, y: 0 } : { opacity: 1, y: 0 }
                }
                animate={{ opacity: 1, y: 0 }}
                exit={isAnimation ? { opacity: 0, y: 0 } : { opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
              >
                {heading}
              </motion.h1>
              <div className="column-indent-both">
                {((image && image.length > 0) ||
                  (video && video.length > 0)) && (
                  <motion.div
                    className="relative mb-8 sm:hidden"
                    initial={
                      isAnimation ? { opacity: 0, y: 0 } : { opacity: 0, y: 0 }
                    }
                    animate={{ opacity: 1, y: 0 }}
                    exit={
                      isAnimation ? { opacity: 0, y: 0 } : { opacity: 1, y: 0 }
                    }
                    transition={{ duration: 1, delay: 0, ease: 'easeInOut' }}
                  >
                    {image && image.length > 0 && (
                      <img
                        src={image[0]?.url}
                        alt=""
                        className="absolute top-0 left-0 object-cover w-full h-auto rounded"
                      />
                    )}
                    {video && video.length > 0 && (
                      <motion.video
                        width="100%"
                        animate={{ opacity: videoIsPlayed ? 1 : 0 }}
                        height="auto"
                        muted
                        playsInline
                        autoPlay="autoplay"
                        loop
                        onPlay={onPlayVideo}
                        className="absolute top-0 left-0 object-cover rounded opacity-0"
                      >
                        <source
                          src={
                            video.find(({ extension }) => extension === 'mp4')
                              ?.url
                          }
                          type='video/mp4; codecs="hvc1"'
                        />
                        <source
                          src={
                            video.find(({ extension }) => extension === 'webm')
                              ?.url
                          }
                          type="video/webm"
                        />
                      </motion.video>
                    )}
                    <span
                      style={{ paddingBottom: `${videoRatioDefault}%` }}
                      className="block w-full rounded shadow-tpmp lg:shadow-dp bg-gray-1"
                    ></span>
                  </motion.div>
                )}
                <motion.div
                  className="u-p link-wrapper"
                  initial={
                    isAnimation ? { opacity: 0, y: 0 } : { opacity: 1, y: 0 }
                  }
                  animate={{ opacity: 1, y: 0 }}
                  exit={
                    isAnimation ? { opacity: 0, y: 0 } : { opacity: 1, y: 0 }
                  }
                  transition={{ duration: 0.7, delay: 0.2, ease: 'easeInOut' }}
                  dangerouslySetInnerHTML={{ __html: body }}
                ></motion.div>
              </div>
            </div>
            <div className="hidden col-2-type-j sm:block">
              <motion.div
                className="sticky top-0"
                initial={
                  isAnimation ? { opacity: 0, y: 50 } : { opacity: 1, y: 0 }
                }
                animate={{ opacity: 1, y: 0 }}
                exit={
                  isAnimation ? { opacity: 0, y: 50 } : { opacity: 1, y: 0 }
                }
                transition={{ duration: 1, delay: 0, ease: 'easeInOut' }}
              >
                {image && image[0] && (
                  <img
                    src={image[0]?.url}
                    alt=""
                    className="absolute top-0 left-0 object-cover w-full h-auto rounded"
                  />
                )}
                {video && video.length > 0 && (
                  <motion.video
                    animate={{ opacity: videoIsPlayed ? 1 : 0 }}
                    width="100%"
                    height="auto"
                    muted
                    playsInline
                    autoPlay="autoplay"
                    loop
                    className="absolute top-0 left-0 object-cover w-full h-full rounded opacity-0"
                    onPlay={onPlayVideo}
                  >
                    <source
                      src={
                        video.find(({ extension }) => extension === 'mp4')?.url
                      }
                      type='video/mp4; codecs="hvc1"'
                    />
                    <source
                      src={
                        video.find(({ extension }) => extension === 'webm')?.url
                      }
                      type="video/webm"
                    />
                  </motion.video>
                )}
                <span
                  style={{ paddingBottom: `${videoRatioDefault}%` }}
                  className="block w-full rounded shadow-tpmp lg:shadow-dp -mt-36 bg-gray-1"
                ></span>
              </motion.div>
            </div>
          </div>
          <div className="row-type-h pb-36">
            <div className="z-20 col-1-type-h">
              <div className="mt-10">
                {colorGroups.map(colorGroup => (
                  <div key={colorGroup.groupHeading} className="mb-20">
                    <h2 className="mb-3 font-bold u-p">
                      {colorGroup.groupHeading}
                    </h2>

                    <div className="grid grid-cols-3 gap-5 pt-8 xl:grid-cols-4">
                      {colorGroup.colorSamples
                        .map(sample => sample.colorSampleDetails[0])
                        .map(sample => (
                          <div
                            key={sample.sampleTitle}
                            className="relative w-full h-20 md:h-24 bg-center bg-cover group pt-[100%] rounded-full"
                            style={{
                              backgroundImage: `url(${sample.image[0].url})`,
                            }}
                          >
                            <div className="opacity-0 group-hover:opacity-100 transition-all absolute bg-white bottom-full left-1/2 shadow-box rounded-[5px] px-3 py-2 text-[15px] pointer-events-none w-32 mb-2.5 -ml-16">
                              <div className="font-bold">
                                {sample.sampleTitle}
                              </div>
                              {sample.description && (
                                <div
                                  className="link-wrapper"
                                  dangerouslySetInnerHTML={{
                                    __html: nl2br(sample.description),
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-2-type-h" />
          </div>
        </div>
      </div>
    </section>
  )
}

export default M25DetailsAndColors
